import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from './useAuth';
import { permissionsAPI } from '../services/api';

export type Permission = 
  | 'clients.create'
  | 'clients.read'
  | 'clients.update'
  | 'clients.delete'
  | 'plans.create'
  | 'plans.read'
  | 'plans.update'
  | 'plans.delete'
  | 'payments.create'
  | 'payments.read'
  | 'payments.update'
  | 'payments.delete'
  | 'payments.verify'
  | 'invoices.create'
  | 'invoices.read'
  | 'invoices.update'
  | 'invoices.delete'
  | 'invoices.generate_pdf'
  | 'caskets.create'
  | 'caskets.read'
  | 'caskets.update'
  | 'caskets.delete'
  | 'plan_templates.create'
  | 'plan_templates.read'
  | 'plan_templates.update'
  | 'plan_templates.delete'
  | 'reports.generate'
  | 'reports.read'
  | 'configuration.read'
  | 'configuration.update'
  | 'configuration.reset'
  | 'users.create'
  | 'users.read'
  | 'users.update'
  | 'users.delete'
  | 'users.reset_password'
  | 'service_activations.create'
  | 'service_activations.read'
  | 'service_activations.update'
  | 'service_activations.cancel';

// Definir permisos por rol
const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  ADMIN: [
    // Administradores tienen todos los permisos
    'clients.create', 'clients.read', 'clients.update', 'clients.delete',
    'plans.create', 'plans.read', 'plans.update', 'plans.delete',
    'payments.create', 'payments.read', 'payments.update', 'payments.delete', 'payments.verify',
    'invoices.create', 'invoices.read', 'invoices.update', 'invoices.delete', 'invoices.generate_pdf',
    'caskets.create', 'caskets.read', 'caskets.update', 'caskets.delete',
    'plan_templates.create', 'plan_templates.read', 'plan_templates.update', 'plan_templates.delete',
    'reports.generate', 'reports.read',
    'configuration.read', 'configuration.update', 'configuration.reset',
    'users.create', 'users.read', 'users.update', 'users.delete', 'users.reset_password',
    'service_activations.create', 'service_activations.read', 'service_activations.update', 'service_activations.cancel'
  ],
  MANAGER: [
    // Managers pueden gestionar casi todo excepto configuración del sistema
    'clients.create', 'clients.read', 'clients.update', 'clients.delete',
    'plans.create', 'plans.read', 'plans.update', 'plans.delete',
    'payments.create', 'payments.read', 'payments.update', 'payments.verify',
    'invoices.create', 'invoices.read', 'invoices.update', 'invoices.generate_pdf',
    'caskets.create', 'caskets.read', 'caskets.update', 'caskets.delete',
    'plan_templates.create', 'plan_templates.read', 'plan_templates.update',
    'reports.generate', 'reports.read',
    'configuration.read',
    'users.read',
    'service_activations.read', 'service_activations.create', 'service_activations.update'
  ],
  EMPLOYEE: [
    // Empleados pueden leer y crear, pero no eliminar ni modificar configuraciones críticas
    'clients.read', 'clients.create', 'clients.update',
    'plans.read', 'plans.create', 'plans.update',
    'payments.create', 'payments.read', 'payments.update',
    'invoices.read', 'invoices.create', 'invoices.update', 'invoices.generate_pdf',
    'caskets.read', 'caskets.create', 'caskets.update',
    'plan_templates.read',
    'reports.read',
    'service_activations.read'
  ]
};

export const usePermissions = () => {
  const { user } = useAuth();

  const { data: permissionsData } = useQuery(
    ['permissions-me', user?.id],
    () => permissionsAPI.getCurrentUserPermissions(),
    {
      enabled: !!user && user.role !== 'PLATFORM_OWNER' && user.role !== 'ADMIN',
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const backendPermissions: Record<string, string[]> | null =
    permissionsData?.data?.data?.permissions ?? null;

  const staticPermissions = useMemo(() => {
    if (!user) return [];
    return ROLE_PERMISSIONS[user.role] || [];
  }, [user]);

  const hasPermission = (permission: Permission): boolean => {
    if (!user) return false;

    if (user.role === 'PLATFORM_OWNER') {
      return false;
    }
    
    // Los administradores tienen acceso a todo
    if (user.role === 'ADMIN') {
      return true;
    }
    
    const [moduleName, action] = permission.split('.');
    const normalizedModuleName = moduleName === 'plans' ? 'plan_templates' : moduleName;

    if (backendPermissions) {
      return (backendPermissions[normalizedModuleName] || []).includes(action);
    }

    return staticPermissions.includes(permission);
  };

  const hasAnyPermission = (permissions: Permission[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissions: Permission[]): boolean => {
    return permissions.every(permission => hasPermission(permission));
  };

  const canAccessModule = (module: string): boolean => {
    if (!user) return false;

    if (user.role === 'PLATFORM_OWNER') {
      return module === 'platform';
    }
    
    // Los administradores pueden acceder a todos los módulos
    if (user.role === 'ADMIN') {
      return true;
    }
    
    // Dashboard siempre accesible para usuarios autenticados
    if (module === 'dashboard') {
      return true;
    }

    const modulePermissions: Record<string, Permission[]> = {
      clients: ['clients.read'],
      'plan-templates': ['plan_templates.read'],
      cobros: ['invoices.read', 'payments.read'],
      payments: ['payments.read'],
      invoices: ['invoices.read'],
      caskets: ['caskets.read'],
      reports: ['reports.read'],
      configuration: ['configuration.read'],
      users: ['users.read'],
      'service-activations': ['service_activations.read'],
    };

    const requiredPermissions = modulePermissions[module];
    return requiredPermissions ? hasAnyPermission(requiredPermissions) : false;
  };

  const getAvailableActions = (resource: string): string[] => {
    if (!user) return [];

    const actions: Record<string, string[]> = {
      clients: ['create', 'read', 'update', 'delete'],
      plans: ['create', 'read', 'update', 'delete'],
      payments: ['create', 'read', 'update', 'delete', 'verify'],
      invoices: ['create', 'read', 'update', 'delete', 'generate_pdf'],
      caskets: ['create', 'read', 'update', 'delete'],
      reports: ['generate', 'read'],
      configuration: ['read', 'update', 'reset'],
      users: ['create', 'read', 'update', 'delete', 'reset_password'],
      service_activations: ['create', 'read', 'update', 'cancel']
    };

    // Los administradores tienen acceso a todas las acciones
    if (user.role === 'ADMIN') {
      return actions[resource] || [];
    }

    const availableActions = actions[resource] || [];
    return availableActions.filter(action => 
      hasPermission(`${resource}.${action}` as Permission)
    );
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessModule,
    getAvailableActions,
    userRole: user?.role,
    isAdmin: user?.role === 'ADMIN',
    isManager: user?.role === 'MANAGER',
    isEmployee: user?.role === 'EMPLOYEE'
  };
};
